/**
 * Composer do chat — textarea + ações de anexo/emoji + envio/gravação.
 */
import React, { useEffect, useRef, type ChangeEvent, type RefObject } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import Textarea from '../../../../components/ui/Textarea';
import { ChatInputActions } from './ChatInputActions';
import { ChatRecordingBar } from './ChatRecordingBar';

/** Alinhado a `.chat-composer__field` (max-height: 120px). */
const COMPOSER_FIELD_MAX_PX = 120;

interface ChatComposerProps {
    chatPlatform?: 'whatsapp' | 'instagram';
    messageText: string;
    setMessageText: (text: string) => void;
    sendValidationError?: string | null;
    isRecording: boolean;
    isPaused: boolean;
    isPreviewPlaying: boolean;
    canResumePreview: boolean;
    previewProgress: number;
    recordingTime: number;
    formatTime: (seconds: number) => string;
    cancelRecording: () => void;
    pauseRecording: () => void;
    resumeRecording: () => void;
    togglePreviewPlayback: () => void;
    seekPreview: (ratio: number) => void;
    showSendButton: boolean;
    startRecording: () => void;
    stopRecording: () => void;
    handleSend: () => void;
    toggleEmojiPicker: () => void;
    showEmojiPicker: boolean;
    emojiButtonRef: RefObject<HTMLButtonElement>;
    mediaInputRef: RefObject<HTMLInputElement>;
    documentInputRef: RefObject<HTMLInputElement>;
    onMediaChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onDocumentChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onOpenContact: () => void;
    onOpenPix: () => void;
    onOpenLocation: () => void;
    handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    handlePaste: (e: React.ClipboardEvent) => void;
}

const SEND_ICON_CLASS = 'size-[18px] rotate-45 text-white';

type TrailingActionProps = Pick<
    ChatComposerProps,
    'isRecording' | 'showSendButton' | 'stopRecording' | 'handleSend' | 'startRecording'
>;

function ComposerTrailingAction({
    isRecording,
    showSendButton,
    stopRecording,
    handleSend,
    startRecording,
}: TrailingActionProps) {
    if (isRecording || showSendButton) {
        const sendAudio = isRecording;
        return (
            <button
                type="button"
                onClick={sendAudio ? stopRecording : handleSend}
                className="chat-composer__icon chat-composer__icon--send"
                title={sendAudio ? 'Enviar áudio' : 'Enviar mensagem'}
                aria-label={sendAudio ? 'Enviar áudio' : 'Enviar mensagem'}
            >
                <AppIcon name="send" className={SEND_ICON_CLASS} aria-hidden />
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={startRecording}
            className="chat-composer__icon"
            title="Gravar áudio"
            aria-label="Gravar áudio"
        >
            <AppIcon name="mic" className="size-5" aria-hidden />
        </button>
    );
}

export function ChatComposer({
    chatPlatform = 'whatsapp',
    messageText,
    setMessageText,
    sendValidationError,
    isRecording,
    showSendButton,
    startRecording,
    stopRecording,
    handleSend,
    toggleEmojiPicker,
    showEmojiPicker,
    emojiButtonRef,
    mediaInputRef,
    documentInputRef,
    onMediaChange,
    onDocumentChange,
    onOpenContact,
    onOpenPix,
    onOpenLocation,
    handleKeyDown,
    handlePaste,
    ...recordingBar
}: ChatComposerProps) {
    const fieldRef = useRef<HTMLTextAreaElement>(null);

    // Clear programático (enviar/anexar) não dispara onInput — altura acompanha o valor.
    useEffect(() => {
        const el = fieldRef.current;
        if (!el) return;
        el.style.height = 'auto';
        if (!messageText) {
            el.style.height = '';
            return;
        }
        el.style.height = `${Math.min(el.scrollHeight, COMPOSER_FIELD_MAX_PX)}px`;
    }, [messageText]);

    const composerClass = [
        'chat-composer flex-1',
        isRecording ? 'min-h-[52px]' : '',
        sendValidationError ? 'input-error-state' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <>
            <div className={composerClass}>
                {isRecording ? (
                    <ChatRecordingBar isRecording {...recordingBar} />
                ) : (
                    <>
                        <ChatInputActions
                            chatPlatform={chatPlatform}
                            isRecording={false}
                            toggleEmojiPicker={toggleEmojiPicker}
                            showEmojiPicker={showEmojiPicker}
                            emojiButtonRef={emojiButtonRef}
                            mediaInputRef={mediaInputRef}
                            documentInputRef={documentInputRef}
                            onMediaChange={onMediaChange}
                            onDocumentChange={onDocumentChange}
                            onOpenContact={onOpenContact}
                            onOpenPix={onOpenPix}
                            onOpenLocation={onOpenLocation}
                        />

                        <Textarea
                            ref={fieldRef}
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            placeholder="Digite sua mensagem..."
                            aria-label="Digite sua mensagem"
                            autoComplete="off"
                            variant="minimal"
                            minRows={1}
                            aria-invalid={!!sendValidationError}
                            aria-describedby={sendValidationError ? 'chat-send-error' : undefined}
                            className="chat-composer__field w-full max-h-[120px] overflow-y-auto text-[16px] md:text-[length:var(--text-base)]"
                        />
                    </>
                )}

                <div className="chat-composer__actions pr-s1">
                    <ComposerTrailingAction
                        isRecording={isRecording}
                        showSendButton={showSendButton}
                        stopRecording={stopRecording}
                        handleSend={handleSend}
                        startRecording={startRecording}
                    />
                </div>
            </div>

            {sendValidationError ? (
                <p id="chat-send-error" className="mt-1.5 px-1 text-xs text-destructive" role="alert">
                    {sendValidationError}
                </p>
            ) : null}
        </>
    );
}
